
import { Fragment } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, CheckCircle, Star, Tablet, Users, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Layout } from "@/components/layout/Layout";
import { SEO } from "@/components/SEO";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import bookCover from "@/assets/book-cover.png?format=webp";
import { PodcastClips } from "@/components/PodcastClips";


const features = [
  {
    icon: Zap,
    title: "No Fluff, Just Action",
    description: "Practical frameworks you can implement today, not theoretical nonsense.",
  },
  {
    icon: Users,
    title: "Built for Agency Life",
    description: "Real strategies from the trenches of creative agencies and fast-paced teams.",
  },
  {
    icon: BookOpen,
    title: "Culturally Aware",
    description: "Written in plain English with cultural context that actually resonates.",
  },
];

const outcomes = [
  "A repeatable system for running projects from kickoff to delivery—without the chaos",
  "Confidence to manage up, push back on scope creep, and protect your team's time",
  "Ready-to-use templates for workbacks, status reports, and stakeholder updates",
  "Clarity on what great agency project management actually looks like—and how to get there",
];

const testimonials = [
  {
    quote: "Clear, personal, and incredibly helpful. The advice is smart, practical, and easy to apply—I even picked up a few new insights. Excellent work.",
    author: "College Professor",
    rating: 5,
  },
  {
    quote: "Thoughtful, motivating, and packed with real-world insight. The examples feel so true to agency life, and the checklists are incredibly useful.",
    author: "Senior Project Manager",
    rating: 5,
  },
  {
    quote: "The rhythm of the writing feels so natural and real—I was left wanting more.",
    author: "Entrepreneur",
    rating: 5,
  },
];

const retailers = [
  { name: "Paperback", brand: "Amazon", href: "https://www.amazon.com/dp/B0GYFGZF3L" },
  { name: "Kindle", brand: "Amazon", href: "https://a.co/d/02CjmGgQ" },
  { name: "Books", brand: "Apple", href: "https://books.apple.com/us/book/wrangling-chaos/id6759410633" },
  { name: "Nook", brand: "Barnes & Noble", href: "https://www.barnesandnoble.com/w/wrangling-chaos-dn-mccier/1149515587?ean=2940183582819" },
  { name: "Kobo", brand: "Rakuten", href: "https://www.kobo.com/us/en/ebook/wrangling-chaos?sId=a1753b50-d23d-4191-9629-cbf95161cece&ssId=lPB0XMHFWL71IcSWAzv75&cPos=1" },
];

export default function HomePage() {
  const { data: latestPost } = useQuery({
    queryKey: ["substack-latest"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("substack-feed");
      if (error) throw error;
      return data as { title: string; link: string; excerpt: string; pubDate: string };
    },
    staleTime: 1000 * 60 * 60, // 1 hour
    retry: 1,
  });

  const websiteJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Wrangling Chaos",
    url: "https://wranglingchaos.org",
    description: "The No-Fluff Guide to Agency Project Management by DN McCier",
    author: {
      "@type": "Person",
      name: "DN McCier",
    },
  };

  return (
    <Layout>
      <SEO
        title="Wrangling Chaos | Agency Project Management Guide"
        description="The no-fluff guide to agency project management. Practical systems for project managers, creatives, and operators. By DN McCier."
        canonical="/"
        jsonLd={websiteJsonLd}
      />
      <section className="relative overflow-hidden bg-primary py-20 md:py-32">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,hsl(var(--brand-yellow-light)/0.3),transparent_50%)]" />
        <div className="container relative">
          <div className="grid gap-12 md:grid-cols-2 md:items-center">
            <div className="space-y-6 animate-fade-in">
              <h1 className="font-display text-4xl font-bold leading-tight text-primary-foreground md:text-5xl lg:text-6xl">
                Lead projects with more clarity and less chaos.
              </h1>
              <p className="text-lg text-primary-foreground/70 max-w-md">
                Stop drowning in deliverables. Start leading with clarity. A practical, 
                no-BS guide for project managers who want real results.
              </p>
              <p className="text-sm font-medium text-primary-foreground/60">
                By DN McCier · 15+ years in agency operations & project management
              </p>
              <div className="flex flex-wrap gap-4 pt-4">
                <Button asChild size="lg" variant="secondary" className="font-semibold">
                  <a href="https://www.amazon.com/dp/B0GYFGZF3L" target="_blank" rel="noopener noreferrer">
                    <BookOpen className="mr-2 h-4 w-4" /> Get on Amazon
                  </a>
                </Button>
                <Button asChild size="lg" variant="outline" className="font-semibold bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground">
                  <a href="https://books2read.com/wranglingchaosdnm" target="_blank" rel="noopener noreferrer">
                    <Tablet className="mr-2 h-4 w-4" /> Get the eBook
                  </a>
                </Button>
              </div>
            </div>
            <div className="relative flex justify-center animate-fade-in" style={{ animationDelay: "0.2s" }}>
              <div className="relative">
                <div className="absolute -inset-4 rounded-2xl bg-primary-foreground/10 blur-2xl" />
                <img 
                  src={bookCover} 
                  alt="Wrangling Chaos book cover - The No-Fluff Guide to Agency Project Management by DN McCier" 
                  className="relative w-64 md:w-80 rounded-lg shadow-bold animate-float"
                  loading="eager"
                  fetchPriority="high"
                  width={320}
                  height={480}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Get the Book Retailer Bar */}
      <section className="bg-background py-10 md:py-14">
        <div className="container">
          <div className="relative mx-auto max-w-6xl group">
            <div className="absolute -inset-1 bg-brand-yellow opacity-20 blur-xl transition-opacity duration-500 group-hover:opacity-30" aria-hidden="true" />
            <div className="relative flex flex-col md:flex-row items-stretch bg-secondary border-y-2 border-brand-yellow overflow-hidden shadow-bold">
              {/* Label */}
              <div className="flex items-center justify-center bg-brand-yellow px-8 py-4 md:py-0">
                <span className="text-secondary font-black text-xl uppercase tracking-tighter whitespace-nowrap">
                  Get the Book:
                </span>
              </div>

              {/* Retailers */}
              <div className="flex-1 flex flex-wrap items-center justify-around gap-4 px-6 py-6 md:py-8">
                {retailers.map((r, i) => (
                  <Fragment key={r.href}>
                    <a
                      href={r.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group/link flex flex-col items-center transition-transform hover:-translate-y-1"
                    >
                      <span className="text-[10px] uppercase tracking-widest text-brand-yellow font-bold mb-1">
                        {r.brand}
                      </span>
                      <span className="text-secondary-foreground font-bold text-sm md:text-base group-hover/link:text-brand-yellow transition-colors">
                        {r.name}
                      </span>
                    </a>
                    {i < retailers.length - 1 && (
                      <div className="h-8 w-px bg-secondary-foreground/20 hidden md:block" aria-hidden="true" />
                    )}
                  </Fragment>
                ))}
              </div>

              {/* Endcap pulse */}
              <div className="hidden lg:flex items-center px-6 bg-secondary-foreground/5">
                <div className="w-2 h-2 rounded-full bg-brand-yellow animate-pulse" aria-hidden="true" />
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* Substack Section */}
      <section className="py-16 md:py-20">
        <div className="container">
          <div className="mx-auto max-w-2xl rounded-2xl border border-border bg-card p-8 md:p-12 text-center">
            <h2 className="font-display text-2xl font-bold md:text-3xl">
              Read the latest from Wrangling Chaos
            </h2>
            {latestPost ? (
              <div className="mt-6 space-y-3">
                <h3 className="font-display text-xl font-semibold text-foreground">
                  {latestPost.title}
                </h3>
                <p className="text-muted-foreground">{latestPost.excerpt}</p>
                <div className="pt-2">
                  <Button asChild size="lg" variant="default" className="font-semibold">
                    <a href={latestPost.link} target="_blank" rel="noopener noreferrer">
                      Read on Substack <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                </div>
              </div>
            ) : (
              <div className="mt-6">
                <p className="text-lg text-muted-foreground">
                  Most projects don't fail at the end. They break at the beginning.
                </p>
                <div className="mt-6">
                  <Button asChild size="lg" variant="default" className="font-semibold">
                    <a href="https://daniellenaomi.substack.com/" target="_blank" rel="noopener noreferrer">
                      Read on Substack <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* What You'll Walk Away With */}
      <section className="py-20 md:py-28">
        <div className="container">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-display text-3xl font-bold md:text-4xl">
              What You'll Walk Away With
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              Practical skills and systems you can implement immediately.
            </p>
          </div>
          <div className="mx-auto mt-12 max-w-3xl">
            <div className="space-y-4">
              {outcomes.map((outcome, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 rounded-xl border border-border bg-card p-5 animate-fade-in"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <CheckCircle className="h-6 w-6 shrink-0 text-brand-yellow-dark mt-0.5" />
                  <span className="text-foreground text-lg">{outcome}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-muted py-20 md:py-28">
        <div className="container">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-display text-3xl font-bold md:text-4xl">
              Why This Book Is Different
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              No academic theory. No corporate buzzwords. Just real strategies that work.
            </p>
          </div>
          <div className="mt-16 grid gap-8 md:grid-cols-3">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className="group rounded-2xl border border-border bg-card p-8 transition-all hover:border-primary hover:shadow-bold animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-brand-yellow-dark transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                  <feature.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-6 font-display text-xl font-bold">{feature.title}</h3>
                <p className="mt-2 text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section - Placeholder */}
      <section className="bg-secondary py-20 md:py-28">
        <div className="container">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-display text-3xl font-bold text-secondary-foreground md:text-4xl">
              What People Are Saying
            </h2>
            <p className="mt-4 text-lg text-secondary-foreground/70">
              Real feedback from real professionals.
            </p>
          </div>
          <div className="mt-16 grid gap-8 md:grid-cols-3">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="rounded-2xl bg-secondary-foreground/5 p-8 animate-fade-in border border-secondary-foreground/10"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex gap-1">
                  {Array.from({ length: testimonial.rating }).map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-brand-yellow text-brand-yellow" />
                  ))}
                </div>
                <blockquote className="mt-4 text-lg text-secondary-foreground italic">
                  "{testimonial.quote}"
                </blockquote>
                <div className="mt-6">
                  <p className="font-semibold text-secondary-foreground">{testimonial.author}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PodcastClips />

      {/* CTA Section */}
      <section className="py-20 md:py-28">
        <div className="container">
          <div className="mx-auto max-w-3xl rounded-3xl bg-primary p-8 text-center md:p-16">
            <h2 className="font-display text-3xl font-bold text-primary-foreground md:text-4xl">
              Ready to Wrangle Your Chaos?
            </h2>
            <p className="mt-4 text-lg text-primary-foreground/80">
              Paperback and eBook available now. Audiobook coming soon.
            </p>
            
            {/* Trust Elements */}
            <div className="mt-6 text-sm text-primary-foreground/70">
              <p>Written by DN McCier — 15+ years in agency project management and creative operations.</p>
            </div>
            
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Button asChild size="lg" variant="secondary" className="font-semibold">
                <a href="https://www.amazon.com/dp/B0GYFGZF3L" target="_blank" rel="noopener noreferrer">
                  <BookOpen className="mr-2 h-4 w-4" /> Get the Paperback
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="font-semibold bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground">
                <a href="https://books2read.com/wranglingchaosdnm" target="_blank" rel="noopener noreferrer">
                  <Tablet className="mr-2 h-4 w-4" /> Get the eBook
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
